/**
 * Notification Utilities
 * Provides unified notifications for upload processing results
 */

/**
 * Snackbar color constants
 */
const SNACKBAR_COLORS = {
	SUCCESS: "rgba(0, 153, 0, 0.72)",
	ERROR: "rgba(153, 0, 0, 0.72)",
	WARNING: "rgba(245, 124, 0, 0.72)",
};

/**
 * Snackbar timeout constants (in milliseconds)
 */
const SNACKBAR_TIMEOUTS = {
	SUCCESS: 3000, // Auto-close success messages after 3 seconds
	ERROR: -1, // Error messages require manual dismissal
	WARNING: -1, // Warning messages require manual dismissal
};

/**
 * Show snackbar notification
 * @param {Object} context - Vue component's this context
 * @param {string} text - Message to display
 * @param {string} color - Snackbar color
 * @param {number} customTimeout - Custom timeout (optional, overrides default based on color)
 */
function showSnackbar(context, text, color, customTimeout) {
	// Determine timeout based on color or use custom value
	let timeout = customTimeout;
	if (timeout === undefined) {
		if (color === SNACKBAR_COLORS.SUCCESS) {
			timeout = SNACKBAR_TIMEOUTS.SUCCESS;
		} else if (color === SNACKBAR_COLORS.ERROR) {
			timeout = SNACKBAR_TIMEOUTS.ERROR;
		} else if (color === SNACKBAR_COLORS.WARNING) {
			timeout = SNACKBAR_TIMEOUTS.WARNING;
		} else {
			timeout = -1; // Default for unknown colors
		}
	}

	const snackbarData = { text, color, timeout };

	// Prefer calling the component's `set_snackbar` mutation method when available.
	// Some tests stub `wrapper.vm.set_snackbar` directly, so calling that
	// ensures the stub is invoked. Fall back to `$store.commit` otherwise.
	if (context && typeof context.set_snackbar === "function") {
		context.set_snackbar(snackbarData);
		return;
	}

	context.$store.commit("set_snackbar", snackbarData);
}

/**
 * Log API error with structured error support
 * @param {Error} error - Error object from axios
 */
function logApiError(error) {
	const errorData = error.response?.data;

	// sendErrorResponse format (structured error)
	if (errorData?.status === "error") {
		console.error(`API Error: ${errorData.message}`);

		if (import.meta.env.DEV && errorData.context) {
			console.error("Error Context:", errorData.context);
		}
	} else {
		// Non-structured error (404, network error, etc.)
		console.error(`Request Error: ${error.message}`);
	}
}

/**
 * Display upload result notification based on Promise.allSettled results
 * Shows individual snackbar for each upload result
 *
 * @param {Object} context - Vue component's this context
 * @param {Array} results - Array of Promise.allSettled results
 * @param {Array} items - Array of items being uploaded (for messages)
 * @param {Function} onSuccess - Callback function when all succeed (optional)
 */
export function handleUploadResults(context, results, items, onSuccess) {
	const successes = results
		.map((result, index) => ({ result, item: items[index] }))
		.filter(({ result }) => result.status === "fulfilled");

	const errors = results
		.map((result, index) => ({ result, item: items[index] }))
		.filter(({ result }) => result.status === "rejected");

	// Display individual success message for each successful upload
	for (const { item } of successes) {
		const namePrefix = item && item.name ? `${item.name} ` : "";
		showSnackbar(
			context,
			`${namePrefix}${context.$t("UPLOAD")}${context.$t("REQUEST")} ${context.$t("successful")}`,
			SNACKBAR_COLORS.SUCCESS,
		);
	}

	// Display individual error message for each failed upload
	// Note: 504 timeout is rare for point cloud uploads as the server returns 200 immediately
	// and processes tiling asynchronously. 504 may occur for synchronous operations like
	// design/lineworks/linearfile uploads if processing takes too long.
	for (const { result, item } of errors) {
		// Log error with structured error support
		logApiError(result.reason);

		const errorStatus =
			typeof result.reason.toJSON === "function"
				? result.reason.toJSON().status
				: result.reason.response?.status;

		if (errorStatus === 504) {
			const namePrefix = item && item.name ? `${item.name}\n` : "";
			showSnackbar(
				context,
				`${namePrefix}${context.$t("entity_upload_wait_msg")}`,
				SNACKBAR_COLORS.WARNING,
			);
		} else {
			// Show standard error message (detailed error is logged to console)
			const namePrefix = item && item.name ? `${item.name} ` : "";
			showSnackbar(
				context,
				`${namePrefix}${context.$t("UPLOAD")} ${context.$t("failed")}`,
				SNACKBAR_COLORS.ERROR,
			);
		}
	}

	// Call success callback only if all succeeded
	// Wait for snackbar rendering and App.vue's watch handler to complete
	if (errors.length === 0 && onSuccess) {
		// Use double $nextTick to ensure all DOM updates and watchers have completed
		context.$nextTick(() => {
			onSuccess();
		});
	}
}

/**
 * Success notification for single upload
 *
 * @param {Object} context - Vue component's this context
 * @param {string} fileName - Name of the uploaded file
 * @param {Object} options - Optional configuration
 * @param {string} options.customMessage - Custom success message (if provided, fileName is ignored)
 */
export function handleSingleUploadSuccess(context, fileName, options = {}) {
	const namePrefix = !options.customMessage && fileName ? `${fileName} ` : "";
	const message = options.customMessage
		? options.customMessage
		: `${namePrefix}${context.$t("UPLOAD")}${context.$t("REQUEST")} ${context.$t("successful")}`;

	showSnackbar(context, message, SNACKBAR_COLORS.SUCCESS);
}

/**
 * Error handling for single upload
 *
 * @param {Object} context - Vue component's this context
 * @param {Error} error - Error object
 * @param {string} fileName - Name of the uploaded file
 */
export function handleSingleUploadError(context, error, fileName) {
	// Log error with structured error support
	logApiError(error);

	const errorStatus =
		typeof error.toJSON === "function"
			? error.toJSON().status
			: error.response?.status;

	// 504 timeout may occur when:
	// - Design/lineworks/linearfile API processing exceeds server timeout
	// - External tiling service (for point clouds) is slow (rare, as server responds immediately)
	// Note: Point cloud uploads via /pointcloud/process return 200 immediately and process asynchronously,
	// so 504 is unlikely for point clouds unless the initial DB insert times out.
	const namePrefix = fileName ? `${fileName}` : "";
	if (errorStatus === 504 || error.code === "ECONNABORTED") {
		const prefix = namePrefix ? `${namePrefix}\n` : "";
		showSnackbar(
			context,
			`${prefix}${context.$t("entity_upload_wait_msg")}`,
			SNACKBAR_COLORS.WARNING,
		);
	} else {
		// Show standard error message (detailed error is logged to console)
		const prefix = namePrefix ? `${namePrefix} ` : "";
		showSnackbar(
			context,
			`${prefix}${context.$t("UPLOAD")} ${context.$t("failed")}`,
			SNACKBAR_COLORS.ERROR,
		);
	}
}

/**
 * Generic error handling (not specific to upload)
 * Suitable for exports, downloads, or other API operations
 *
 * @param {Object} context - Vue component's this context
 * @param {Error} error - Error object
 * @param {string} customMessage - Custom error message prefix (operation name)
 */
export function handleCustomError(context, error, customMessage) {
	// Log error with structured error support
	logApiError(error);

	// Show default error message (detailed error is logged to console)
	showSnackbar(context, customMessage, SNACKBAR_COLORS.ERROR);
}

/**
 * Show success notification (auto-dismisses after 3 seconds by default)
 *
 * @param {Object} context - Vue component's this context
 * @param {string} message - Success message to display
 * @param {number} timeout - Optional timeout in milliseconds (default: 3000)
 */
export function notifySuccess(context, message, timeout) {
	showSnackbar(context, message, SNACKBAR_COLORS.SUCCESS, timeout);
}

/**
 * Show error notification (requires manual dismissal by default)
 *
 * @param {Object} context - Vue component's this context
 * @param {string} message - Error message to display
 * @param {number} timeout - Optional timeout in milliseconds (default: -1 for manual dismissal)
 */
export function notifyError(context, message, timeout) {
	showSnackbar(context, message, SNACKBAR_COLORS.ERROR, timeout);
}

/**
 * Show warning notification (requires manual dismissal by default)
 *
 * @param {Object} context - Vue component's this context
 * @param {string} message - Warning message to display
 * @param {number} timeout - Optional timeout in milliseconds (default: -1 for manual dismissal)
 */
export function notifyWarning(context, message, timeout) {
	showSnackbar(context, message, SNACKBAR_COLORS.WARNING, timeout);
}

/**
 * Show custom notification with specified color and timeout
 *
 * @param {Object} context - Vue component's this context
 * @param {string} message - Message to display
 * @param {string} color - Snackbar color (rgba format)
 * @param {number} timeout - Optional timeout in milliseconds (if omitted, determined by color)
 */
export function notify(context, message, color, timeout) {
	showSnackbar(context, message, color, timeout);
}
